import React, { useEffect, useState, useCallback } from "react";
import {
  Avatar,
  Card,
  Col,
  Row,
  Tag,
  Typography,
  Space,
  Button,
  List,
  Tooltip,
} from "antd";
import {
  SwapOutlined,
  ExportOutlined,
  ImportOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import HeaderEstandar from "../../components/layout/headers/HeaderEstandar";
import AppLayout from "../../components/layout/AppLayout";
import Loader from "../../components/loader/Loader";
import { estadoConfig } from "../../utils/getEstadoProp";
import { getCardStyle } from "../../utils/getCardStyle";
import { formatFecha } from "../../utils/formatFecha";
import { getTipoTagProps } from "../../utils/getTipoTragProp";
import GETListarDisponibles from "../../helpers/ordenes/GETListarDisponibles";
import PullToRefresh from "react-simple-pull-to-refresh";

const { Text } = Typography;

const tipoOperacionConfig = {
  REU: { color: "purple", text: "Reubicación", icon: <SwapOutlined /> },
  OME: { color: "volcano", text: "Egreso", icon: <ExportOutlined /> },
  OMI: { color: "green", text: "Ingreso", icon: <ImportOutlined /> },
};

const CardOrden = React.memo(function CardOrden({
  orden,
  darkMode,
  isExpanded,
  onToggle,
  onNavigate,
}) {
  const estado = estadoConfig[orden.estado];

  const tipo = React.useMemo(() => {
    if (!orden.numero) return null;
    if (orden.numero.includes("REU")) return tipoOperacionConfig.REU;
    if (orden.numero.includes("OME")) return tipoOperacionConfig.OME;
    if (orden.numero.includes("OMI")) return tipoOperacionConfig.OMI;
    return null;
  }, [orden.numero]);

  let detallesList = [];
  let parseError = false;
  if (isExpanded && orden.ctdsma > 1 && orden.detallesma) {
    try {
      const parsed = JSON.parse(orden.detallesma || "[]");
      if (Array.isArray(parsed)) {
        detallesList = parsed;
      } else {
        parseError = true;
      }
    } catch (e) {
      console.error("Error parsing detallesma:", e);
      parseError = true;
    }
  }

  return (
    <Col span={24} style={{ display: "flex" }}>
      <Card
        hoverable
        onClick={() => onNavigate(orden)}
        styles={{ body: { padding: 16 } }}
        style={{ ...getCardStyle(darkMode), height: "100%", flex: 1 }}
      >
        <Row align="middle" justify="space-between">
          <Space align="start">
            <Avatar
              src="/assets/mov_articulos.png"
              size={60}
              style={{ backgroundColor: "#ffcc00" }}
            />
            <div>
              <Text
                strong
                style={{
                  fontSize: "22px",
                  color: darkMode ? "#fff" : "#000",
                }}
              >
                {orden.numero}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 16 }}>
                {formatFecha(orden.fecha)}
              </Text>
            </div>
          </Space>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: 120,
              marginTop: 8,
            }}
          >
            <Tag
              color={estado?.color}
              icon={estado?.icon}
              style={{ fontSize: 17, width: 120, textAlign: "center" }}
            >
              {estado?.text}
            </Tag>
          </div>
        </Row>

        <Space
          direction="vertical"
          size="small"
          style={{ marginTop: 16, width: "100%" }}
        >
          {orden.sucursalinicial && (
            <div>
              <Tag
                color={darkMode ? "geekblue" : "blue"}
                style={{ fontSize: 16 }}
              >
                SUC
              </Tag>
              <Text style={{ color: darkMode ? "#ccc" : "#333", fontSize: 16 }}>
                {orden.sucursalinicial}
              </Text>
            </div>
          )}

          {orden.comprobantesma && (
            <div>
              <Tag color="purple" style={{ fontSize: 16 }}>
                WMS
              </Tag>
              <Text style={{ color: darkMode ? "#ccc" : "#333", fontSize: 16 }}>
                {orden.comprobantesma}
              </Text>
            </div>
          )}

          {orden.comprobanteerp && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Tag color="purple" style={{ fontSize: "16px" }}>
                ERP
              </Tag>
              <Text
                style={{ color: darkMode ? "#ccc" : "#333", fontSize: "16px" }}
              >
                {orden.comprobanteerp}
              </Text>
              {orden.ctdsma > 1 && (
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(e, orden.idomam);
                  }}
                  style={{
                    marginLeft: "auto",
                    minWidth: 30,
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 21,
                    flexShrink: 0,
                  }}
                >
                  {isExpanded ? "−" : "+"}
                </Button>
              )}
            </div>
          )}

          {isExpanded && orden.ctdsma > 1 && (
            <div
              style={{
                padding: 12,
                backgroundColor: darkMode ? "#2f2f2f" : "#f9f9f9",
                borderRadius: 8,
              }}
            >
              <Text
                strong
                style={{ fontSize: 15, color: darkMode ? "#fff" : "#000" }}
              >
                Detalle SMA ({orden.ctdsma}):
              </Text>

              {parseError ? (
                <Text
                  type="danger"
                  style={{ fontSize: 15, display: "block", marginTop: 8 }}
                >
                  Error al cargar los detalles
                </Text>
              ) : (
                <List
                  size="small"
                  dataSource={detallesList}
                  style={{ marginTop: 8, border: "none" }}
                  renderItem={(item, index) => (
                    <List.Item style={{ padding: "4px 0" }}>
                      <Tooltip title={item.descri} placement="top">
                        <Text
                          style={{
                            fontSize: 14,
                            padding: 8,
                            color: darkMode ? "#ccc" : "#555",
                            display: "block",
                            whiteSpace: "pre-line", // respeta saltos de línea
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            maxWidth: "100%",
                          }}
                        >
                          {`${item.nrosma}\n${
                            item.descri.length > 70
                              ? `${item.descri.substring(0, 70)}…`
                              : item.descri
                          }`}
                        </Text>
                      </Tooltip>
                    </List.Item>
                  )}
                  locale={{ emptyText: "No hay detalles disponibles" }}
                />
              )}
            </div>
          )}

          <div style={{ display: "flex" }}>
            {tipo && (
              <Tag color={tipo.color} icon={tipo.icon} style={{ fontSize: 16 }}>
                {tipo.text}
              </Tag>
            )}
            {orden.tiposma && (
              <Tag
                color={getTipoTagProps(orden.tiposma).color}
                icon={getTipoTagProps(orden.tiposma).icon}
                style={{ marginRight: "8px", fontSize: 16 }}
              >
                {orden.tiposma}
              </Tag>
            )}
          </div>
        </Space>
      </Card>
    </Col>
  );
});

const Ordenes = ({ darkMode, setDarkMode }) => {
  const { loggedUser } = useAuth();
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await GETListarDisponibles("OMA");
        if (Array.isArray(response?.data)) {
          setOrdenes(response.data);
        } else {
          console.warn("La respuesta no es un array válido:", response);
          setOrdenes([]);
        }
      } catch (error) {
        console.error("Error al cargar órdenes:", error);
        setOrdenes([]);
      } finally {
        setLoading(false);
      }
    };

    if (loggedUser) {
      fetchData();
    }
  }, [loggedUser]);

  const handleToggle = useCallback((e, id) => {
    e.stopPropagation();
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleNavigate = useCallback(
    (orden) => {
      if (orden.idomam) {
        const tiposma = orden.tiposma;
        const esREU = orden.numero?.includes("REU") || false;
        navigate(`/ordenes/${orden.idomam}`, { state: { esREU, tiposma } });
      }
    },
    [navigate],
  );

  const headerComponent = (
    <HeaderEstandar data={{ headerTitle: "Mis Órdenes" }} />
  );

  return (
    <AppLayout
      darkMode={darkMode}
      setDarkMode={setDarkMode}
      header={headerComponent}
    >
      <PullToRefresh
        onRefresh={async () => {
          setLoading(true);
          try {
            const response = await GETListarDisponibles("OMA");
            if (Array.isArray(response?.data)) {
              setOrdenes(response.data);
            } else {
              setOrdenes([]);
            }
          } catch (error) {
            console.error("Error al refrescar órdenes:", error);
            setOrdenes([]);
          } finally {
            setLoading(false);
          }
        }}
        maxPullDownDistance={80}
        refreshingContent={null}
        pullingContent={null}
      >
        <div
          style={{
            height: "100%",
            padding: "8px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {loading && <Loader />}

          <Row gutter={[8, 8]} justify="start" align="stretch">
            {ordenes.length === 0 && !loading ? (
              <Col span={24}>
                <Card
                  style={{
                    textAlign: "center",
                    backgroundColor: darkMode ? "#1f1f1f" : "#fafafa",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "32px",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: "19px" }}>
                    No hay órdenes disponibles.
                  </Text>
                </Card>
              </Col>
            ) : (
              ordenes.map((orden) => (
                <CardOrden
                  key={orden.idomam || orden.numero}
                  orden={orden}
                  darkMode={darkMode}
                  isExpanded={!!expanded[orden.idomam]}
                  onToggle={handleToggle}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </Row>
        </div>
      </PullToRefresh>
    </AppLayout>
  );
};

export default Ordenes;
